// ForgotPassword.js - Squarespace Design
import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './firebase'; // Import your firebase config
import { useNavigate, useLocation } from 'react-router-dom';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  const handleSubmit = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Check your email for password reset instructions');
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later');
          break;
        default:
          setError('Failed to send reset email. Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleBackToLogin = () => {
    // Pass the redirect parameter back to login if it exists
    if (redirectUrl) {
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'radial-gradient(ellipse at 50% 0%, #131022 0%, #05040d 70%)' }}>
      {/* Top Navigation - Squarespace style */}
      <div className="flex justify-between items-center p-8 md:p-12">
        {/* Back Arrow */}
        <button 
          onClick={() => navigate('/map')}
          className="w-10 h-10 rounded-full bg-white/10 border border-white/25 hover:border-[#cbab6a]/70 hover:bg-white/20 flex items-center justify-center transition-all duration-300"
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Sign In */}
        <button 
          onClick={handleBackToLogin}
          className="text-xs text-white hover:opacity-70 transition-all duration-300 uppercase tracking-[0.2em] font-light"
        >
          Sign In
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-8 md:px-12 pb-16">
        <div className="w-full max-w-2xl">
          
          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-extralight text-center text-white mb-4 tracking-wide">
            Reset Password
          </h1>

          {/* Subtitle */}
          <p className="text-center text-gray-500 mb-16 font-light text-sm uppercase tracking-[0.15em]">
            Enter your email to receive reset instructions
          </p>

          {/* Success Message */}
          {message && (
            <div className="mb-8 p-6 bg-gray-900 border border-gray-800 mx-auto max-w-lg">
              <p className="text-green-400 text-sm text-center font-light">✓ {message}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-6 bg-gray-900 border border-gray-800 mx-auto max-w-lg">
              <p className="text-red-400 text-sm text-center font-light">✕ {error}</p>
            </div>
          )}

          {/* Form Container */}
          <div className="max-w-lg mx-auto">
            {/* Email Field */}
            <div className="mb-12">
              <label className="block text-xs text-gray-500 uppercase tracking-[0.2em] mb-4 font-light">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pb-3 border-0 border-b border-gray-700/70 bg-transparent text-white placeholder-gray-600 focus:outline-none focus:border-[#e5c883] transition-all duration-500 font-light"
                placeholder="name@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Send Email Button */}
            <button
              onClick={handleSubmit}
              disabled={isLoading || !email}
              className="w-full rounded-full bg-white hover:bg-[#ffedc4] disabled:bg-gray-700 disabled:text-gray-500 text-black py-4 text-xs font-medium uppercase tracking-[0.2em] transition-all duration-300 disabled:cursor-not-allowed mt-8 hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
            >
              {isLoading ? 'Sending Instructions...' : 'Send Reset Instructions'}
            </button>

            {/* Back to Sign In Link */}
            <div className="text-center mt-8">
              <button 
                onClick={handleBackToLogin}
                className="text-gray-500 text-xs hover:text-white transition-all duration-300 uppercase tracking-[0.15em] font-light"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Security Notice - Fixed at bottom */}
      <div className="text-center pb-8 px-8 mt-16">
        <p className="text-xs text-gray-600 font-light">
          Secure Login with reCAPTCHA subject to Google{' '}
          <a href="https://policies.google.com/terms?hl=en" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-all duration-300">Terms</a> &{' '}
          <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-all duration-300">Privacy</a>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;